<script setup>
import { RouterLink } from "vue-router";
import { iconHref } from "~/utils/icons.js";

/**
 * Sticky breadcrumb bar placed inside a content box.
 *
 * @prop {Array<{ text: String, href?: String }>} breadcrumbs
 *   Uses `text` key to match GlBreadcrumb's items prop shape — a future swap to
 *   <GlBreadcrumb :items="breadcrumbs"> requires no changes in callers.
 *   The last entry is always treated as the current page (no link, aria-current).
 */
defineProps({
  breadcrumbs: { default: () => [], type: Array }
});
</script>

<template>
  <div
    class="rounded-t-xl border-b border-[var(--gl-border-color-default)] bg-[var(--gl-background-color-default)] px-5 py-3"
  >
    <nav aria-label="Breadcrumb">
      <ol class="flex items-center gap-1.5 text-sm">
        <li>
          <RouterLink
            to="/projects"
            class="flex items-center text-[var(--gl-text-color-subtle)] hover:text-[var(--gl-text-color-default)]"
            title="Home"
          >
            <svg class="h-4 w-4" aria-hidden="true">
              <use :href="iconHref('home')" />
            </svg>
          </RouterLink>
        </li>
        <template v-for="(crumb, index) in breadcrumbs" :key="index">
          <li class="text-[var(--gl-text-color-subtle)]" aria-hidden="true">
            <svg class="h-3 w-3" aria-hidden="true">
              <use :href="iconHref('chevron-right')" />
            </svg>
          </li>
          <li
            :class="
              index === breadcrumbs.length - 1
                ? 'font-medium text-[var(--gl-text-color-default)]'
                : 'text-[var(--gl-text-color-subtle)]'
            "
            :aria-current="index === breadcrumbs.length - 1 ? 'page' : undefined"
          >
            <RouterLink
              v-if="crumb.href"
              :to="crumb.href"
              class="text-[var(--gl-text-color-subtle)] hover:text-[var(--gl-text-color-default)] hover:underline"
              >{{ crumb.text }}</RouterLink
            >
            <span v-else>{{ crumb.text }}</span>
          </li>
        </template>
      </ol>
    </nav>
  </div>
</template>
